import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import {
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'

import ConfirmDialog from './shared/ConfirmDialog'
import ErrorBanner from './shared/ErrorBanner'
import useAuth from '../hooks/useAuth'
import getEnvironmentVariables from '../helpers/getEnvironmentVariables'
import { canManageComms, commsManagerRole } from '../helpers/permissions'
import axiosInstance from '../services/axios/axiosConfig'
import { endpoints } from '../services/axios/endpoints'
import { errorMessage } from '../services/axios/errorMessage'
import { BRAND, ICON, SURFACE, TEXT } from '../theme/tokens'

/**
 * The sender addresses an email template can choose (the old app's "Manage
 * Sender Emails"): the list, adding one, and removing one.
 *
 * The API checks an address's domain against the ones that can send and
 * answers why it refuses one, so the reason is shown as is. Removing only
 * retires the address: templates that use it keep sending from it, and
 * adding it again brings it back. Changing the list is for the comms
 * manager role (comms-tool-dev on dev, comms-tool-prod on production), as
 * deleting a template is; others see it read-only.
 *
 * @param {{onChange?: function}} props `onChange` runs after an add or remove,
 *     so the template form can reload its sender list.
 */
const SenderEmails = ({ onChange }) => {
    const { user } = useAuth()
    const env = getEnvironmentVariables()
    const canManage = canManageComms(user?.permissions, env)
    const [list, setList] = useState(null)
    const [loadError, setLoadError] = useState('')
    const [email, setEmail] = useState('')
    const [displayName, setDisplayName] = useState('')
    const [busy, setBusy] = useState(false)
    const [actionError, setActionError] = useState('')
    const [toRemove, setToRemove] = useState(null)

    const load = useCallback(async () => {
        setLoadError('')
        try {
            const res = await axiosInstance.get(endpoints.getSenderEmails, {
                silentToast: true,
                silentErrorToast: true,
            })
            setList(res?.data?.sender_emails ?? [])
        } catch (err) {
            setLoadError(errorMessage(err.response))
            setList([])
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    /** Run one change, then reload the list and tell the caller. */
    const change = async (request, failure) => {
        setBusy(true)
        setActionError('')
        try {
            await request()
            await load()
            onChange?.()
            return true
        } catch (err) {
            setActionError(`${failure}: ${errorMessage(err.response)}`)
            return false
        } finally {
            setBusy(false)
        }
    }

    const add = async () => {
        if (!email.trim()) return
        const added = await change(
            () =>
                axiosInstance.post(
                    endpoints.createSenderEmail,
                    {
                        email: email.trim(),
                        display_name: displayName.trim() || null,
                    },
                    { silentErrorToast: true }
                ),
            `${email.trim()} was not added`
        )
        if (added) {
            setEmail('')
            setDisplayName('')
        }
    }

    const remove = () => {
        const entry = toRemove
        setToRemove(null)
        change(
            () =>
                axiosInstance.delete(endpoints.deleteSenderEmail(entry.id), {
                    silentErrorToast: true,
                }),
            `${entry.email} was not removed`
        )
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 0.5,
                }}
            >
                <AlternateEmailIcon
                    sx={{ fontSize: ICON.lg, color: BRAND.orange }}
                />
                <Typography variant="h6" sx={{ color: TEXT.primary }}>
                    Sender Emails
                </Typography>
            </Box>
            <Typography
                variant="caption"
                component="p"
                sx={{ color: TEXT.muted, mb: 2 }}
            >
                The addresses an email template can be sent from. Removing one
                keeps the templates that use it sending; it only leaves this
                list.
            </Typography>

            {(loadError || actionError) && (
                <Box sx={{ mb: 2 }}>
                    <ErrorBanner
                        message={
                            actionError ||
                            `The sender emails could not be loaded: ${loadError}`
                        }
                    />
                </Box>
            )}

            {list === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={20} sx={{ color: BRAND.orange }} />
                </Box>
            ) : list.length === 0 && !loadError ? (
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.muted, mb: 2 }}
                >
                    None added yet. Templates send from no-reply@bhanzu.com
                    unless they set another address.
                </Typography>
            ) : (
                <Box
                    component="ul"
                    sx={{ listStyle: 'none', p: 0, m: 0, mb: 2 }}
                    aria-label="Sender emails"
                >
                    {list.map((entry) => (
                        <Box
                            component="li"
                            key={entry.id ?? entry.email}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                py: 1,
                                borderBottom: `1px solid ${SURFACE.border}`,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.primary,
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {entry.email}
                                </Typography>
                                {entry.display_name && (
                                    <Typography
                                        variant="caption"
                                        component="p"
                                        sx={{ color: TEXT.muted }}
                                    >
                                        {entry.display_name}
                                    </Typography>
                                )}
                            </Box>
                            {canManage && (
                                <Button
                                    variant="outlined"
                                    disabled={busy}
                                    onClick={() => setToRemove(entry)}
                                    aria-label={`Remove ${entry.email}`}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {canManage ? (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                    }}
                >
                    <TextField
                        size="small"
                        label="Email"
                        placeholder="classes@bhanzu.com"
                        // The API's ALLOWED_SENDER_EMAIL_DOMAINS
                        // (CommunicationManagementService utils/constants.py).
                        helperText="Only bhanzu.com and bhanzu.us addresses can be added."
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ flex: 2, minWidth: 200 }}
                    />
                    <TextField
                        size="small"
                        label="Display name (optional)"
                        placeholder="Bhanzu Classes"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        sx={{ flex: 1, minWidth: 160 }}
                    />
                    <Button
                        variant="contained"
                        onClick={add}
                        disabled={busy || !email.trim()}
                    >
                        Add sender
                    </Button>
                </Box>
            ) : (
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.muted }}
                >
                    Changing this list needs the{' '}
                    {commsManagerRole(env) ?? 'comms manager'} role.
                </Typography>
            )}

            <ConfirmDialog
                open={Boolean(toRemove)}
                title="Remove this sender email?"
                message={
                    toRemove
                        ? `${toRemove.email} leaves the list new templates choose from. Templates that already use it keep sending from it.`
                        : ''
                }
                confirmLabel="Remove"
                destructive
                onConfirm={remove}
                onCancel={() => setToRemove(null)}
            />
        </Box>
    )
}

SenderEmails.propTypes = {
    onChange: PropTypes.func,
}

export default SenderEmails
